import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  TrendingUp, TrendingDown, DollarSign, Home, Calendar, 
  Users, Truck, UtensilsCrossed, Car, FileSpreadsheet,
  Loader2, RefreshCw, ArrowUpRight, ArrowDownRight,
  Building, PieChart, BarChart3, Target, Award, Download
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart as RePieChart, Pie, Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import SubscriptionPaywall from './SubscriptionPaywall';
import ComprehensiveProfitCalculator from './ComprehensiveProfitCalculator';
import { analyticsAPI, subscriptionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useSearchParams } from 'react-router-dom';

const COLORS = ['#1F3A68', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const AnalyticsDashboard = () => {
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('12months');
  
  // Data state
  const [overviewData, setOverviewData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [profitStays, setProfitStays] = useState(null);
  const [profitProperties, setProfitProperties] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [marginTrend, setMarginTrend] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [vendorLeaderboard, setVendorLeaderboard] = useState(null);
  const [vendorStats, setVendorStats] = useState(null);
  const [upsellData, setUpsellData] = useState(null);
  const [upsellSummary, setUpsellSummary] = useState(null);
  const [guestLTV, setGuestLTV] = useState(null);
  const [dealData, setDealData] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // Check subscription status
  const checkSubscription = useCallback(async () => {
    try {
      const token = getAuthToken();
      const status = await subscriptionAPI.checkAnalyticsAccess(token);
      setSubscriptionStatus(status);
      
      // Handle successful subscription return from Stripe
      const subscriptionParam = searchParams.get('subscription');
      const sessionId = searchParams.get('session_id');
      
      if (subscriptionParam === 'success' && sessionId) {
        try {
          const checkoutStatus = await subscriptionAPI.getCheckoutStatus(sessionId, token);
          if (checkoutStatus.status === 'active') {
            toast({
              title: 'Subscription Activated!',
              description: 'You now have access to Analytics Pro.',
            });
            setSubscriptionStatus({ hasAccess: true });
          }
        } catch (e) {
          console.error('Error checking subscription status:', e);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscriptionStatus({ hasAccess: false, upgradeRequired: true });
    } finally {
      setCheckingSubscription(false);
    }
  }, [getAuthToken, searchParams, toast]);

  // Fetch overview data
  const fetchOverview = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await analyticsAPI.getOverview(token);
      setOverviewData(data);
    } catch (error) {
      if (error.response?.status === 403) {
        setSubscriptionStatus({ hasAccess: false, upgradeRequired: true });
      } else {
        console.error('Error fetching overview:', error);
      }
    }
  }, [getAuthToken]);

  // Fetch tab-specific data
  const fetchTabData = useCallback(async (tab) => {
    setTabLoading(true);
    try {
      const token = getAuthToken();
      
      switch (tab) {
        case 'revenue':
          const revData = await analyticsAPI.getRevenue(token, period);
          setRevenueData(revData);
          break;
        case 'profit':
          // Use C2 Profit Graph APIs
          const [summaryData, staysData, propsData, costData, trendData] = await Promise.all([
            analyticsAPI.getProfitSummary(token, period),
            analyticsAPI.getProfitStays(token, period, 1, 20),
            analyticsAPI.getProfitByProperty(token, period),
            analyticsAPI.getProfitCostBreakdown(token, period),
            analyticsAPI.getProfitMarginTrend(token, period)
          ]);
          setProfitData(summaryData);
          setProfitStays(staysData);
          setProfitProperties(propsData);
          setCostBreakdown(costData);
          setMarginTrend(trendData);
          break;
        case 'vendors':
          // Use C3 Vendor Scoring APIs
          const [leaderboard, stats] = await Promise.all([
            analyticsAPI.getVendorLeaderboard(token, 'score', 'desc'),
            analyticsAPI.getVendorStatsSummary(token)
          ]);
          setVendorLeaderboard(leaderboard);
          setVendorStats(stats);
          // Keep legacy data for backward compatibility
          const venData = await analyticsAPI.getVendors(token, period);
          setVendorData(venData);
          break;
        case 'upsell':
          // Use C4 Upsell APIs
          const [upsellSumm, ltvData] = await Promise.all([
            analyticsAPI.getUpsellSummary(token, period),
            analyticsAPI.getGuestLTV(token, period, 20)
          ]);
          setUpsellSummary(upsellSumm);
          setGuestLTV(ltvData);
          // Keep legacy data
          const upsData = await analyticsAPI.getUpsell(token, period);
          setUpsellData(upsData);
          break;
        case 'deals':
          // Use C5 Deal ROI APIs
          const dealSummary = await analyticsAPI.getDealsSummary(token);
          setDealData(dealSummary);
          break;
        default:
          break;
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setSubscriptionStatus({ hasAccess: false, upgradeRequired: true });
      } else {
        console.error(`Error fetching ${tab} data:`, error);
        toast({
          title: 'Error',
          description: `Failed to load ${tab} analytics`,
          variant: 'destructive'
        });
      }
    } finally {
      setTabLoading(false);
    }
  }, [getAuthToken, period, toast]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    if (subscriptionStatus?.hasAccess) {
      setLoading(true);
      fetchOverview().finally(() => setLoading(false));
    }
  }, [fetchOverview, subscriptionStatus]);

  useEffect(() => {
    if (subscriptionStatus?.hasAccess && activeTab !== 'overview' && activeTab !== 'calculator') {
      fetchTabData(activeTab);
    }
  }, [activeTab, period, fetchTabData, subscriptionStatus]);

  const formatGBP = (amount) => {
    const num = parseFloat(amount) || 0;
    return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

  // Show loading state
  if (checkingSubscription) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
      </div>
    );
  }

  // Show paywall if no subscription
  if (!subscriptionStatus?.hasAccess) {
    return (
      <SubscriptionPaywall 
        prices={subscriptionStatus?.prices}
        onSubscribed={() => checkSubscription()}
      />
    );
  }

  // Period selector component
  const PeriodSelector = () => (
    <div className="flex gap-2">
      {['3months', '6months', '12months', 'ytd'].map((p) => (
        <Button
          key={p}
          variant={period === p ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPeriod(p)}
          className={period === p ? 'bg-[#1F3A68]' : ''}
        >
          {p === '3months' ? '3M' : p === '6months' ? '6M' : p === '12months' ? '12M' : 'YTD'}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1F3A68]">Analytics Pro</h2>
          <p className="text-gray-600">Business Intelligence & Performance Insights</p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
          PRO Subscription Active
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white p-1 rounded-lg shadow-sm">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
            Overview
          </TabsTrigger>
          <TabsTrigger value="revenue" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
            Revenue
          </TabsTrigger>
          <TabsTrigger value="profit" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
            Profit
          </TabsTrigger>
          <TabsTrigger value="vendors" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
            Vendors
          </TabsTrigger>
          <TabsTrigger value="upsell" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
            Upsell
          </TabsTrigger>
          <TabsTrigger value="deals" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
            Deals
          </TabsTrigger>
          <TabsTrigger value="calculator" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
            Calculator
          </TabsTrigger>
        </TabsList>

        {/* ========== OVERVIEW TAB ========== */}
        <TabsContent value="overview" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
            </div>
          ) : overviewData ? (
            <>
              {/* KPI Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-[#1F3A68] to-[#1F3A68]/80 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign size={24} />
                      {overviewData.kpis.totalRevenue.change !== 0 && (
                        <div className={`flex items-center text-sm ${overviewData.kpis.totalRevenue.change > 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {overviewData.kpis.totalRevenue.change > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                          {formatPercent(overviewData.kpis.totalRevenue.change)}
                        </div>
                      )}
                    </div>
                    <div className="text-2xl font-bold">{formatGBP(overviewData.kpis.totalRevenue.current)}</div>
                    <div className="text-white/80 text-sm">Revenue This Month</div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Calendar size={24} />
                      {overviewData.kpis.totalBookings.change !== 0 && (
                        <div className={`flex items-center text-sm ${overviewData.kpis.totalBookings.change > 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {overviewData.kpis.totalBookings.change > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                          {formatPercent(overviewData.kpis.totalBookings.change)}
                        </div>
                      )}
                    </div>
                    <div className="text-2xl font-bold">{overviewData.kpis.totalBookings.current}</div>
                    <div className="text-white/80 text-sm">Bookings This Month</div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp size={24} />
                    </div>
                    <div className="text-2xl font-bold">{formatGBP(overviewData.kpis.adr.value)}</div>
                    <div className="text-white/80 text-sm">ADR (Avg Daily Rate)</div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <BarChart3 size={24} />
                    </div>
                    <div className="text-2xl font-bold">{formatGBP(overviewData.kpis.revpar.value)}</div>
                    <div className="text-white/80 text-sm">RevPAR</div>
                  </CardContent>
                </Card>
              </div>

              {/* Secondary Stats */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Home className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#1F3A68]">{overviewData.kpis.propertyCount}</div>
                        <div className="text-sm text-gray-600">Active Properties</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-emerald-100 rounded-lg">
                        <Calendar className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-700">{overviewData.kpis.totalNights}</div>
                        <div className="text-sm text-gray-600">Nights Booked</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gray-100 rounded-lg">
                        <DollarSign className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-700">{formatGBP(overviewData.kpis.totalRevenue.previous)}</div>
                        <div className="text-sm text-gray-600">Last Month Revenue</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68]">Quick Access</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-5 gap-4">
                    <Button variant="outline" className="h-20 flex flex-col gap-2" onClick={() => setActiveTab('revenue')}>
                      <TrendingUp size={24} className="text-[#1F3A68]" />
                      <span>Revenue Analytics</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex flex-col gap-2" onClick={() => setActiveTab('profit')}>
                      <DollarSign size={24} className="text-emerald-600" />
                      <span>Profit Analysis</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex flex-col gap-2" onClick={() => setActiveTab('vendors')}>
                      <Users size={24} className="text-amber-600" />
                      <span>Vendor Insights</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex flex-col gap-2" onClick={() => setActiveTab('upsell')}>
                      <Target size={24} className="text-purple-600" />
                      <span>Upsell Metrics</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex flex-col gap-2" onClick={() => setActiveTab('deals')}>
                      <Building size={24} className="text-red-600" />
                      <span>Deal ROI</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <BarChart3 size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-gray-700 mb-2">No Data Available</h3>
                <p className="text-gray-600">Start making bookings to see your analytics</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== REVENUE TAB ========== */}
        <TabsContent value="revenue" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F3A68]">Revenue Intelligence</h3>
            <PeriodSelector />
          </div>

          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
            </div>
          ) : revenueData ? (
            <>
              {/* Revenue KPIs */}
              <div className="grid md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-[#1F3A68]">{formatGBP(revenueData.summary.totalRevenue)}</div>
                    <div className="text-sm text-gray-600">Total Revenue</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{formatGBP(revenueData.summary.adr)}</div>
                    <div className="text-sm text-gray-600">ADR</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{formatGBP(revenueData.summary.revpar)}</div>
                    <div className="text-sm text-gray-600">RevPAR</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{revenueData.summary.totalNights}</div>
                    <div className="text-sm text-gray-600">Total Nights</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-gray-700">{revenueData.summary.totalBookings}</div>
                    <div className="text-sm text-gray-600">Bookings</div>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue Over Time Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68]">Revenue Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueData.monthlyChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={revenueData.monthlyChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => `£${v}`} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => formatGBP(v)} />
                        <Legend />
                        <Bar dataKey="revenue" name="Revenue" fill="#1F3A68" radius={[4, 4, 0, 0]} />
                        <Line type="monotone" dataKey="adr" name="ADR" stroke="#10b981" strokeWidth={2} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">No data available</div>
                  )}
                </CardContent>
              </Card>

              {/* Revenue by Property */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68]">Revenue by Property</CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueData.revenueByProperty.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold">Property</th>
                            <th className="text-right p-3 font-semibold">Revenue</th>
                            <th className="text-right p-3 font-semibold">Nights</th>
                            <th className="text-right p-3 font-semibold">Bookings</th>
                            <th className="text-right p-3 font-semibold">ADR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueData.revenueByProperty.map((prop, idx) => (
                            <tr key={prop.propertyId} className="border-b hover:bg-slate-50">
                              <td className="p-3 font-medium">{prop.title}</td>
                              <td className="p-3 text-right font-bold text-[#1F3A68]">{formatGBP(prop.revenue)}</td>
                              <td className="p-3 text-right">{prop.nights}</td>
                              <td className="p-3 text-right">{prop.bookings}</td>
                              <td className="p-3 text-right text-emerald-600">{formatGBP(prop.adr)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No property data available</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">Select a period to view revenue data</CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== PROFIT TAB (C2) ========== */}
        <TabsContent value="profit" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F3A68]">Profit Intelligence</h3>
            <PeriodSelector />
          </div>

          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
            </div>
          ) : profitData ? (
            <>
              {/* Profit Summary from Profit Graph */}
              <div className="grid md:grid-cols-5 gap-4">
                <Card className="bg-slate-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-slate-700">{formatGBP(profitData.totalGross)}</div>
                    <div className="text-sm text-gray-600">Gross Revenue</div>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{formatGBP(profitData.platformFeeTotal + profitData.stripeFeeTotal)}</div>
                    <div className="text-sm text-gray-600">Platform + Stripe Fees</div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{formatGBP(profitData.vendorCostsTotal)}</div>
                    <div className="text-sm text-gray-600">Vendor Costs</div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{formatGBP(profitData.refundsTotal)}</div>
                    <div className="text-sm text-gray-600">Refunds</div>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{formatGBP(profitData.netProfit)}</div>
                    <div className="text-sm text-gray-600">Net Profit ({profitData.avgProfitMarginPct}%)</div>
                  </CardContent>
                </Card>
              </div>

              {/* Cost Breakdown Pie Chart */}
              {costBreakdown && (
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1F3A68]">Cost Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <RePieChart>
                          <Pie
                            data={costBreakdown.breakdown.filter(b => b.value > 0)}
                            dataKey="value"
                            nameKey="category"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, value }) => `${name}: £${value}`}
                          >
                            {costBreakdown.breakdown.map((entry, idx) => (
                              <Cell key={idx} fill={entry.color || COLORS[idx % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => formatGBP(v)} />
                        </RePieChart>
                      </ResponsiveContainer>
                      <div className="text-center mt-2 text-lg font-bold">
                        Total Costs: {formatGBP(costBreakdown.totalCosts)}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Margin Trend */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1F3A68]">Profit Margin Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {marginTrend && marginTrend.trend.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <ComposedChart data={marginTrend.trend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthName" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left" tickFormatter={(v) => `£${v}`} tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v, name) => name === 'profitMarginPct' ? `${v}%` : formatGBP(v)} />
                            <Legend />
                            <Bar yAxisId="left" dataKey="netProfit" name="Net Profit" fill="#10b981" radius={[4, 4, 0, 0]} />
                            <Line yAxisId="right" type="monotone" dataKey="profitMarginPct" name="Margin %" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6' }} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[250px] flex items-center justify-center text-gray-500">No trend data</div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Profit Per Stay Table */}
              {profitStays && profitStays.stays.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1F3A68]">Profit Per Stay</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold">Booking</th>
                            <th className="text-left p-3 font-semibold">Property</th>
                            <th className="text-center p-3 font-semibold">Nights</th>
                            <th className="text-right p-3 font-semibold">Gross</th>
                            <th className="text-right p-3 font-semibold">Costs</th>
                            <th className="text-right p-3 font-semibold">Net Profit</th>
                            <th className="text-right p-3 font-semibold">Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profitStays.stays.map((stay) => (
                            <tr key={stay.bookingId} className="border-b hover:bg-slate-50">
                              <td className="p-3 font-mono text-sm">{stay.bookingRef || stay.bookingId.slice(-8)}</td>
                              <td className="p-3">{stay.propertyTitle}</td>
                              <td className="p-3 text-center">{stay.nights}</td>
                              <td className="p-3 text-right">{formatGBP(stay.grossRevenue)}</td>
                              <td className="p-3 text-right text-orange-600">{formatGBP(stay.totalCosts)}</td>
                              <td className="p-3 text-right font-bold text-emerald-600">{formatGBP(stay.netProfit)}</td>
                              <td className="p-3 text-right">
                                <Badge className={stay.profitMarginPct >= 50 ? 'bg-emerald-100 text-emerald-800' : stay.profitMarginPct >= 30 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                                  {stay.profitMarginPct}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Property Profit Ranking */}
              {profitProperties && profitProperties.properties.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1F3A68]">Property Profit Ranking</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold">Rank</th>
                            <th className="text-left p-3 font-semibold">Property</th>
                            <th className="text-center p-3 font-semibold">Bookings</th>
                            <th className="text-right p-3 font-semibold">Gross</th>
                            <th className="text-right p-3 font-semibold">Vendor/Turnover</th>
                            <th className="text-right p-3 font-semibold">Net Profit</th>
                            <th className="text-right p-3 font-semibold">Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profitProperties.properties.map((prop, idx) => (
                            <tr key={prop.propertyId} className="border-b hover:bg-slate-50">
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  idx === 0 ? 'bg-amber-100 text-amber-700' :
                                  idx === 1 ? 'bg-gray-200 text-gray-700' :
                                  idx === 2 ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  #{idx + 1}
                                </span>
                              </td>
                              <td className="p-3 font-medium">{prop.propertyTitle}</td>
                              <td className="p-3 text-center">{prop.totalBookings}</td>
                              <td className="p-3 text-right">{formatGBP(prop.grossRevenue)}</td>
                              <td className="p-3 text-right text-red-600">{formatGBP(prop.vendorCostPerTurnover)}</td>
                              <td className="p-3 text-right font-bold text-emerald-600">{formatGBP(prop.netProfit)}</td>
                              <td className="p-3 text-right">
                                <Badge className={prop.profitMarginPct >= 50 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                                  {prop.profitMarginPct}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">Select a period to view profit data</CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== VENDORS TAB (C3) ========== */}
        <TabsContent value="vendors" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F3A68]">Vendor Performance Scoring</h3>
            <PeriodSelector />
          </div>

          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
            </div>
          ) : (vendorLeaderboard || vendorData) ? (
            <>
              {/* Vendor Summary from C3 */}
              <div className="grid md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-[#1F3A68]">{vendorStats?.totalVendors || vendorData?.summary?.vendorCount || 0}</div>
                    <div className="text-sm text-gray-600">Total Vendors</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{vendorStats?.avgScore?.toFixed(1) || 'N/A'}</div>
                    <div className="text-sm text-gray-600">Avg Score</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{vendorStats?.avgOnTimePct?.toFixed(1) || 'N/A'}%</div>
                    <div className="text-sm text-gray-600">Avg On-Time</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{vendorStats?.totalTasks || vendorData?.summary?.totalTasks || 0}</div>
                    <div className="text-sm text-gray-600">Total Tasks</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{formatGBP(vendorStats?.totalCost || vendorData?.summary?.totalVendorCost || 0)}</div>
                    <div className="text-sm text-gray-600">Total Spend</div>
                  </CardContent>
                </Card>
              </div>

              {/* C3 Vendor Leaderboard with Scores */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68]">Vendor Performance Leaderboard</CardTitle>
                </CardHeader>
                <CardContent>
                  {(vendorLeaderboard?.leaderboard?.length > 0 || vendorData?.vendorLeaderboard?.length > 0) ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold">Rank</th>
                            <th className="text-left p-3 font-semibold">Vendor</th>
                            <th className="text-center p-3 font-semibold">Tasks</th>
                            <th className="text-center p-3 font-semibold">On-Time %</th>
                            <th className="text-center p-3 font-semibold">Avg Rating</th>
                            <th className="text-right p-3 font-semibold">Avg Cost</th>
                            <th className="text-center p-3 font-semibold">Disputes</th>
                            <th className="text-right p-3 font-semibold">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(vendorLeaderboard?.leaderboard || vendorData?.vendorLeaderboard || []).map((vendor, idx) => (
                            <tr key={vendor.vendorId} className="border-b hover:bg-slate-50">
                              <td className="p-3">
                                {idx < 3 ? (
                                  <Award className={`h-5 w-5 ${idx === 0 ? 'text-amber-500' : idx === 1 ? 'text-gray-400' : 'text-orange-400'}`} />
                                ) : (
                                  <span className="text-gray-500">#{vendor.rank || idx + 1}</span>
                                )}
                              </td>
                              <td className="p-3 font-medium">{vendor.vendorName}</td>
                              <td className="p-3 text-center">{vendor.completedTasks || vendor.totalTasks}</td>
                              <td className="p-3 text-center">
                                <Badge className={vendor.onTimePct >= 90 ? 'bg-emerald-100 text-emerald-800' : vendor.onTimePct >= 70 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                                  {vendor.onTimePct?.toFixed(1) || 'N/A'}%
                                </Badge>
                              </td>
                              <td className="p-3 text-center">{vendor.avgRating?.toFixed(1) || 'N/A'} ★</td>
                              <td className="p-3 text-right">{formatGBP(vendor.avgCost || vendor.avgTaskCost)}</td>
                              <td className="p-3 text-center">
                                {vendor.disputeCount > 0 ? (
                                  <Badge className="bg-red-100 text-red-800">{vendor.disputeCount}</Badge>
                                ) : (
                                  <span className="text-emerald-600">0</span>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                <Badge className={vendor.score >= 70 ? 'bg-emerald-100 text-emerald-800' : vendor.score >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                                  {vendor.score?.toFixed(1) || 'N/A'}/100
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No vendor performance data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Score Formula Explanation */}
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-slate-700 mb-2">Score Formula</h4>
                  <p className="text-sm text-slate-600">
                    <strong>Score</strong> = (40% × On-Time %) + (30% × Rating) + (20% × Cost Efficiency) - (10% × Dispute Rate)
                  </p>
                  <div className="mt-2 text-xs text-slate-500">
                    <span className="mr-4">🟢 On-Time: 40%</span>
                    <span className="mr-4">⭐ Rating: 30%</span>
                    <span className="mr-4">💰 Cost Efficiency: 20%</span>
                    <span>⚠️ Disputes: -10%</span>
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Vendor Costs (legacy chart) */}
              {vendorData?.monthlyChart?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1F3A68]">Monthly Vendor Costs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={vendorData.monthlyChart}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => `£${v}`} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v, name) => [name === 'cost' ? formatGBP(v) : v, name === 'cost' ? 'Cost' : 'Tasks']} />
                        <Legend />
                        <Bar dataKey="cost" name="Cost" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">Select a period to view vendor data</CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== UPSELL TAB ========== */}
        <TabsContent value="upsell" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F3A68]">Upsell Intelligence</h3>
            <PeriodSelector />
          </div>

          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
            </div>
          ) : upsellData ? (
            <>
              {/* Upsell Summary */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Food Upsell */}
                <Card className="border-2 border-amber-200">
                  <CardHeader className="bg-amber-50">
                    <CardTitle className="text-amber-800 flex items-center gap-2">
                      <UtensilsCrossed className="h-5 w-5" />
                      Food Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-amber-600">{formatGBP(upsellData.food.totalRevenue)}</div>
                        <div className="text-sm text-gray-600">Total Revenue</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-amber-600">{upsellData.food.totalOrders}</div>
                        <div className="text-sm text-gray-600">Total Orders</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div className="text-center">
                        <div className="text-xl font-bold text-gray-700">{upsellData.food.conversionRate}%</div>
                        <div className="text-xs text-gray-500">Conversion Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-gray-700">{formatGBP(upsellData.food.avgPerStay)}</div>
                        <div className="text-xs text-gray-500">Avg Per Stay</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Taxi Upsell */}
                <Card className="border-2 border-purple-200">
                  <CardHeader className="bg-purple-50">
                    <CardTitle className="text-purple-800 flex items-center gap-2">
                      <Car className="h-5 w-5" />
                      Taxi Rides
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{formatGBP(upsellData.taxi.totalRevenue)}</div>
                        <div className="text-sm text-gray-600">Total Revenue</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{upsellData.taxi.totalRides}</div>
                        <div className="text-sm text-gray-600">Total Rides</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div className="text-center">
                        <div className="text-xl font-bold text-gray-700">{upsellData.taxi.conversionRate}%</div>
                        <div className="text-xs text-gray-500">Conversion Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-gray-700">{formatGBP(upsellData.taxi.avgPerStay)}</div>
                        <div className="text-xs text-gray-500">Avg Per Stay</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Upsell Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68]">Upsell Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  {(upsellSummary?.monthlyChart?.length > 0 || upsellData?.monthlyChart?.length > 0) ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={upsellSummary?.monthlyChart || upsellData.monthlyChart}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => `£${v}`} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => formatGBP(v)} />
                        <Legend />
                        <Bar dataKey="foodRevenue" name="Food" fill="#f59e0b" stackId="a" />
                        <Bar dataKey="taxiRevenue" name="Taxi" fill="#8b5cf6" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">No upsell data available</div>
                  )}
                </CardContent>
              </Card>

              {/* C4: Guest LTV Table */}
              {guestLTV && guestLTV.guests?.length > 0 && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-[#1F3A68]">Guest Lifetime Value (LTV)</CardTitle>
                      <div className="text-sm text-gray-600">
                        Avg LTV: <span className="font-bold text-emerald-600">{formatGBP(guestLTV.summary?.avgLTV || 0)}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold">Guest</th>
                            <th className="text-center p-3 font-semibold">Bookings</th>
                            <th className="text-right p-3 font-semibold">Booking Rev</th>
                            <th className="text-right p-3 font-semibold">Food</th>
                            <th className="text-right p-3 font-semibold">Taxi</th>
                            <th className="text-right p-3 font-semibold">Total LTV</th>
                          </tr>
                        </thead>
                        <tbody>
                          {guestLTV.guests.map((guest, idx) => (
                            <tr key={guest.guestId} className="border-b hover:bg-slate-50">
                              <td className="p-3 font-medium">{guest.guestName}</td>
                              <td className="p-3 text-center">{guest.bookings}</td>
                              <td className="p-3 text-right">{formatGBP(guest.bookingRevenue)}</td>
                              <td className="p-3 text-right text-amber-600">{formatGBP(guest.foodRevenue)}</td>
                              <td className="p-3 text-right text-purple-600">{formatGBP(guest.taxiRevenue)}</td>
                              <td className="p-3 text-right font-bold text-emerald-600">{formatGBP(guest.totalLTV)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">Select a period to view upsell data</CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== DEALS TAB (C5) ========== */}
        <TabsContent value="deals" className="space-y-6">
          <h3 className="text-lg font-semibold text-[#1F3A68]">Deal ROI Intelligence</h3>

          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
            </div>
          ) : dealData ? (
            <>
              {/* Deal Summary - C5 */}
              <div className="grid md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-[#1F3A68]">{dealData.summary?.totalDeals || 0}</div>
                    <div className="text-sm text-gray-600">Total Deals</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{formatGBP(dealData.summary?.totalMonthlyRent || 0)}</div>
                    <div className="text-sm text-gray-600">Monthly Rent</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-[#1F3A68]">{formatGBP(dealData.summary?.totalRevenue || 0)}</div>
                    <div className="text-sm text-gray-600">Total Revenue</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{formatGBP(dealData.summary?.totalProfit || 0)}</div>
                    <div className="text-sm text-gray-600">Total Profit</div>
                  </CardContent>
                </Card>
                <Card className={(dealData.summary?.overallROI || 0) >= 0 ? 'bg-emerald-50' : 'bg-red-50'}>
                  <CardContent className="p-4 text-center">
                    <div className={`text-2xl font-bold ${(dealData.summary?.overallROI || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {dealData.summary?.overallROI || 0}%
                    </div>
                    <div className="text-sm text-gray-600">Overall ROI</div>
                  </CardContent>
                </Card>
              </div>

              {/* C5 Deal Table with Risk Score */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68]">R2R Deal Performance & Risk Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  {(dealData.deals?.length > 0) ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold">Property</th>
                            <th className="text-right p-3 font-semibold">Monthly Rent</th>
                            <th className="text-center p-3 font-semibold">Months</th>
                            <th className="text-right p-3 font-semibold">Revenue</th>
                            <th className="text-right p-3 font-semibold">Profit</th>
                            <th className="text-right p-3 font-semibold">ROI</th>
                            <th className="text-center p-3 font-semibold">Payback</th>
                            <th className="text-center p-3 font-semibold">Risk</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dealData.deals.map((deal) => (
                            <tr key={deal.dealId || deal.offerId} className="border-b hover:bg-slate-50">
                              <td className="p-3 font-medium">{deal.propertyTitle}</td>
                              <td className="p-3 text-right">{formatGBP(deal.monthlyRent)}</td>
                              <td className="p-3 text-center">{deal.monthsActive?.toFixed(1) || 0}</td>
                              <td className="p-3 text-right text-[#1F3A68]">{formatGBP(deal.totalRevenue || 0)}</td>
                              <td className={`p-3 text-right font-bold ${(deal.totalProfit || deal.netProfit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatGBP(deal.totalProfit || deal.netProfit || 0)}
                              </td>
                              <td className="p-3 text-right">
                                <Badge className={(deal.roiPct || deal.roi || 0) >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                                  {deal.roiPct || deal.roi || 0}%
                                </Badge>
                              </td>
                              <td className="p-3 text-center text-sm">
                                {deal.paybackMonths ? (
                                  deal.paybackMonths > 100 ? '100+' : `${deal.paybackMonths.toFixed(1)} mo`
                                ) : 'N/A'}
                              </td>
                              <td className="p-3 text-center">
                                <Badge className={
                                  deal.riskLevel === 'low' ? 'bg-emerald-100 text-emerald-800' :
                                  deal.riskLevel === 'medium' ? 'bg-amber-100 text-amber-800' :
                                  'bg-red-100 text-red-800'
                                }>
                                  {deal.riskLevel || 'N/A'} ({deal.riskScore || 0})
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No R2R deals found</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">Loading deal data...</CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== CALCULATOR TAB ========== */}
        <TabsContent value="calculator">
          <ComprehensiveProfitCalculator onSave={() => {}} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
